import { Link } from "react-router-dom";
import { ArrowRight, Check, Phone, Mail } from "lucide-react";

const PACKAGES = [
    {
        id: "trunk-bay",
        name: "Trunk Bay",
        subtitle: "Basic Pricing",
        accent: "Intimate ceremonies",
        price: 140,
        hors: "Choice of 3 Hors d'Oeuvres",
        starches: 1,
        vegetables: 1,
        entrees: 2,
        notes: [
            "Choice of salad",
            "Dessert bar",
            "Dinner rolls & butter",
            "Iced tea, lemonade & water",
            "Coffee & tea service",
        ],
        image:
            "https://images.unsplash.com/photo-1519741497674-611481863552?auto=format&fit=crop&w=1800&q=90",
    },
    {
        id: "salt-pond",
        name: "Salt Pond",
        subtitle: "Popular Pricing",
        accent: "Most chosen",
        price: 165,
        hors: "Choice of 3 Hors d'Oeuvres Butlered · Choice of 2 Hors d'Oeuvres Stationed",
        starches: 2,
        vegetables: 1,
        entrees: 2,
        notes: [
            "Choice of salad",
            "Dessert bar",
            "Dinner rolls & butter",
            "Iced tea, lemonade & water",
            "Coffee & tea service",
        ],
        image:
            "https://images.unsplash.com/photo-1464366400600-7168b8af9bc3?auto=format&fit=crop&w=1800&q=90",
        featured: true,
    },
    {
        id: "hawksnest",
        name: "Hawksnest",
        subtitle: "Complete Pricing",
        accent: "Extravagant",
        price: 195,
        hors: "Choice of 5 Hors d'Oeuvres Butlered · Choice of 3 Hors d'Oeuvres Stationed",
        starches: 2,
        vegetables: 2,
        entrees: "Carving Station + Choice of 2 Additional Entrées",
        notes: [
            "Choice of salad",
            "Dessert bar",
            "Dinner rolls & butter",
            "Iced tea, lemonade & water",
            "Coffee & tea service",
        ],
        image:
            "https://images.unsplash.com/photo-1606216794074-735e91aa2c92?auto=format&fit=crop&w=1800&q=90",
    },
];

const HERO_IMG =
    "https://images.unsplash.com/photo-1519741497674-611481863552?auto=format&fit=crop&w=2880&q=90";

export default function WeddingsPage() {
    return (
        <div data-testid="weddings-page">
            {/* HERO */}
            <section className="relative min-h-[80vh] w-full overflow-hidden grain">
                <img
                    src={HERO_IMG}
                    alt="Coral Bay Catering wedding"
                    className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-b from-forest-900/70 via-forest-900/40 to-forest-900/70" />
                <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 pt-44 lg:pt-56 pb-32 text-sand-100">
                    <span
                        className="overline !text-sand-100/80"
                        data-testid="weddings-hero-overline"
                    >
                        Weddings & Banquets · St. John USVI
                    </span>
                    <h1
                        className="font-serif font-light text-5xl sm:text-6xl lg:text-7xl xl:text-8xl mt-6 max-w-4xl leading-[1.05]"
                        data-testid="weddings-hero-headline"
                    >
                        Memories for <em className="italic text-palm-400">a lifetime,</em>
                        <br />
                        plated by the Caribbean.
                    </h1>
                    <p className="mt-8 max-w-2xl text-base lg:text-lg text-sand-100/85 leading-relaxed">
                        Coral Bay Catering prides itself on a fresh, creative, and
                        personalized approach to make your exquisite event one of a
                        kind. We offer delectable gourmet food for both extravagant
                        weddings and intimate ceremonies. Our skilled staff of chefs,
                        servers, and event coordinators will handle every facet of
                        your special day.
                    </p>
                    <div className="mt-10 flex flex-col sm:flex-row gap-4">
                        <Link
                            to="/contact?subject=Wedding%20Inquiry"
                            data-testid="weddings-inquire-btn"
                            className="inline-flex items-center justify-center gap-2 bg-sand-100 text-forest-900 px-8 py-4 rounded-sm hover:bg-white transition-colors text-sm tracking-wide"
                        >
                            Inquire about your date
                            <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
                        </Link>
                        <a
                            href="tel:3404236611"
                            data-testid="weddings-call-btn"
                            className="inline-flex items-center justify-center gap-2 bg-transparent border border-sand-100/60 text-sand-100 px-8 py-4 rounded-sm hover:bg-sand-100/10 transition-colors text-sm tracking-wide"
                        >
                            <Phone className="w-4 h-4" strokeWidth={1.5} />
                            Call us
                        </a>
                    </div>
                </div>
            </section>

            {/* INTRO */}
            <section className="max-w-5xl mx-auto px-6 lg:px-10 py-24 lg:py-32 text-center">
                <span className="overline">Three packages, infinite menus</span>
                <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-forest-700 mt-5 leading-[1.05]">
                    From intimate elopements
                    <br />
                    to <em className="italic">extravagant celebrations.</em>
                </h2>
                <p className="text-forest-700/70 leading-relaxed mt-8 max-w-2xl mx-auto">
                    Each package is fully customizable around your guest list,
                    dietary needs, and dream menu. Choose your tier, then we'll
                    design the menu together. Pricing below is per person.
                </p>
            </section>

            {/* PACKAGES */}
            <section
                className="bg-sand-200/60 py-24 lg:py-32 border-y border-palm-400/20"
                data-testid="wedding-packages-section"
            >
                <div className="max-w-7xl mx-auto px-6 lg:px-10">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-10">
                        {PACKAGES.map((p, i) => (
                            <WeddingPackageCard key={p.id} pkg={p} index={i} />
                        ))}
                    </div>
                </div>
            </section>

            {/* WHAT'S INCLUDED */}
            <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-32">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
                    <div className="lg:col-span-5">
                        <span className="overline">What's included</span>
                        <h2 className="font-serif text-4xl md:text-5xl text-forest-700 mt-5 leading-tight">
                            Every detail, <em className="italic">handled.</em>
                        </h2>
                        <p className="text-forest-700/70 leading-relaxed mt-6 max-w-md">
                            Every package includes our chefs, servers, and event
                            coordinator on-site, plus we can guide you through
                            everything else — flowers, décor, rentals, music, valet
                            and transportation — so you focus on the moment.
                        </p>
                    </div>
                    <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-6">
                        {[
                            ["Skilled chefs", "On-site cooking by our culinary team"],
                            ["Trained servers", "Coursed service throughout the night"],
                            ["Event coordinator", "Day-of management & timeline"],
                            ["Dinner rolls & butter", "Fresh-baked & warmed"],
                            ["Beverage service", "Iced tea, lemonade, water"],
                            ["Coffee & tea", "End-of-night service"],
                            ["Vendor recommendations", "Flowers, music, rentals & more"],
                            ["Custom menu design", "Built around your guests"],
                        ].map(([t, d]) => (
                            <div
                                key={t}
                                className="border border-palm-400/30 rounded-sm p-5 bg-white"
                            >
                                <div className="font-serif text-xl text-forest-700">{t}</div>
                                <div className="text-sm text-forest-700/65 mt-1">{d}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* NOTE */}
            <section className="bg-forest-700 text-sand-100 py-16">
                <div className="max-w-4xl mx-auto px-6 lg:px-10 text-center">
                    <span className="overline !text-palm-400">A small note</span>
                    <p className="font-serif text-2xl md:text-3xl mt-5 leading-relaxed italic">
                        "Some appetizers require an upcharge as noted. All packages
                        also include our standard 20% gratuity, 5% processing fee,
                        and the appropriate service fee for your guest count."
                    </p>
                    <p className="text-sand-100/70 text-sm mt-6 tracking-wide">
                        Custom cakes available for in-villa & full-service events.
                        Pricing non-negotiable. Reservations confirmed with 50% deposit.
                    </p>
                </div>
            </section>

            {/* CTA */}
            <section className="max-w-4xl mx-auto px-6 lg:px-10 py-24 lg:py-32 text-center">
                <span className="overline">Ready to begin?</span>
                <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-forest-700 mt-5 leading-[1.05]">
                    Let's design your day.
                </h2>
                <p className="text-forest-700/70 leading-relaxed mt-6 max-w-xl mx-auto">
                    Tell us your date, your dream guest count, and which package
                    speaks to you. We'll come back with a custom menu within 48
                    hours.
                </p>
                <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
                    <Link
                        to="/contact?subject=Wedding%20Inquiry"
                        data-testid="weddings-cta-inquire"
                        className="inline-flex items-center justify-center gap-2 bg-forest-700 text-sand-100 px-8 py-4 rounded-sm hover:bg-forest-900 transition-colors text-sm tracking-wide"
                    >
                        Start your inquiry
                        <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
                    </Link>
                    <a
                        href="mailto:coralbaycatering@gmail.com?subject=Wedding%20Inquiry"
                        data-testid="weddings-cta-email"
                        className="inline-flex items-center justify-center gap-2 border border-forest-700 text-forest-700 px-8 py-4 rounded-sm hover:bg-forest-700 hover:text-sand-100 transition-colors text-sm tracking-wide"
                    >
                        <Mail className="w-4 h-4" strokeWidth={1.5} />
                        Email us
                    </a>
                </div>
            </section>
        </div>
    );
}

function WeddingPackageCard({ pkg, index }) {
    return (
        <article
            data-testid={`wedding-package-${pkg.id}`}
            className={`relative bg-white border rounded-sm overflow-hidden hover:-translate-y-1 transition-transform duration-300 animate-fade-up ${
                pkg.featured
                    ? "border-forest-700 shadow-lg shadow-forest-900/10"
                    : "border-palm-400/30"
            }`}
            style={{ animationDelay: `${index * 100}ms` }}
        >
            {pkg.featured && (
                <div className="absolute top-4 right-4 z-10 bg-forest-700 text-sand-100 px-3 py-1 text-[10px] tracking-[0.22em] uppercase rounded-sm">
                    Most chosen
                </div>
            )}
            <div className="aspect-[4/3] overflow-hidden bg-sand-200">
                <img
                    src={pkg.image}
                    alt={pkg.name}
                    className="w-full h-full object-cover"
                    loading="lazy"
                />
            </div>
            <div className="p-7 flex flex-col gap-3">
                <span className="overline">{pkg.accent}</span>
                <h3 className="font-serif text-3xl md:text-4xl text-forest-700 leading-tight">
                    {pkg.name}
                </h3>
                <div className="text-xs uppercase tracking-[0.18em] text-forest-400">
                    {pkg.subtitle}
                </div>

                <div className="mt-3 pt-4 border-t border-palm-400/30 flex items-end justify-between">
                    <div>
                        <div className="font-serif text-4xl text-emerald-600 leading-none">
                            ${pkg.price}
                        </div>
                        <div className="text-xs text-forest-700/60 mt-1">per person</div>
                    </div>
                </div>

                <div className="mt-4 space-y-3">
                    <Row label="Hors d'oeuvres" value={pkg.hors} />
                    <Row
                        label="Dinner"
                        value={
                            typeof pkg.entrees === "string"
                                ? pkg.entrees
                                : `Choice of ${pkg.entrees} entrées`
                        }
                    />
                    <Row label="Sides" value={`${pkg.starches} starch · ${pkg.vegetables} vegetable`} />
                </div>

                <ul className="mt-4 pt-4 border-t border-palm-400/20 space-y-2">
                    {pkg.notes.map((n) => (
                        <li
                            key={n}
                            className="flex items-start gap-2 text-sm text-forest-700/80"
                        >
                            <Check
                                className="w-4 h-4 mt-0.5 text-forest-400 shrink-0"
                                strokeWidth={1.5}
                            />
                            {n}
                        </li>
                    ))}
                </ul>

                <Link
                    to={`/contact?subject=Wedding%20Inquiry%20-%20${encodeURIComponent(pkg.name)}`}
                    data-testid={`wedding-inquire-${pkg.id}`}
                    className={`mt-6 inline-flex items-center justify-center gap-2 px-6 py-3 rounded-sm text-sm tracking-wide transition-colors ${
                        pkg.featured
                            ? "bg-forest-700 text-sand-100 hover:bg-forest-900"
                            : "border border-forest-700 text-forest-700 hover:bg-forest-700 hover:text-sand-100"
                    }`}
                >
                    Inquire about {pkg.name}
                    <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
                </Link>
                <p className="text-xs text-forest-700/55 mt-1">
                    *Some appetizers may require an upcharge.
                </p>
            </div>
        </article>
    );
}

function Row({ label, value }) {
    return (
        <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-forest-400">
                {label}
            </div>
            <div className="text-sm text-forest-700/85 mt-1 leading-relaxed">
                {value}
            </div>
        </div>
    );
}
